// ADR 8.9.26g §2 — which of the two token blocks is in force.
//
// `darkMode: 'class'`, so something has to put `.dark` on the document. That
// something is here rather than in a component: the resolution is a pure
// function of two inputs — a stored override and the media query — and putting
// it in a component would make it re-run on every render of whatever mounted
// it, and be untestable without a tree.
//
// **The override beats the media query, and only a recognised override does.**
// A stored value that is neither `light` nor `dark` — a stale key, a
// hand-edited value, a half-written migration — falls back to
// `prefers-color-scheme` rather than to a hard-coded default: the media query
// is always an answer, and the constant would be a guess.
//
// ADR 30.9.26i §6 is the ADR that added the UI: the account page's Theme
// chips write through `setScheme`, and "System default" through `clearScheme`.
// A web-only divergence — Android has no toggle.

/** The two schemes, which are exactly the two token blocks in `index.css`. */
export type Scheme = 'light' | 'dark'

/** Where an override lives. Namespaced, because `localStorage` is one bucket. */
export const THEME_KEY = 'lingara.theme'

/** The class `tailwind.config.js`'s `darkMode: 'class'` looks for. */
export const DARK_CLASS = 'dark'

const DARK_QUERY = '(prefers-color-scheme: dark)'

/** A stored value only counts when it names a scheme this app has. */
function asScheme(stored: string | null): Scheme | null {
  return stored === 'light' || stored === 'dark' ? stored : null
}

/**
 * The resolution itself, as data in and an answer out (AC9).
 *
 * Both inputs are parameters rather than reads so the rule can be asserted
 * without a DOM, a `localStorage` or a `matchMedia` — which is also what stops
 * the rule being re-spelt at the one call site below.
 */
export function resolveScheme(stored: string | null, prefersDark: boolean): Scheme {
  return asScheme(stored) ?? (prefersDark ? 'dark' : 'light')
}

/** Put the resolved scheme on an element — `<html>`, in practice. */
export function applyScheme(root: Element, scheme: Scheme): void {
  root.classList.toggle(DARK_CLASS, scheme === 'dark')
}

/** What a settings surface would call. Stores the override, then applies it. */
export function setScheme(scheme: Scheme): void {
  window.localStorage.setItem(THEME_KEY, scheme)
  applyScheme(document.documentElement, scheme)
}

/**
 * ADR 30.9.26i §6 — back to "follow the system": forget the override, then
 * apply the media query's answer (at this moment; there is no live listener).
 */
export function clearScheme(): void {
  try {
    window.localStorage.removeItem(THEME_KEY)
  } catch {
    // An unwritable store still gets the system scheme applied below.
  }
  applyScheme(document.documentElement, resolveScheme(null, window.matchMedia(DARK_QUERY).matches))
}

/** The stored override as the chips read it: absent or unrecognised is System (`null`). */
export function storedScheme(): Scheme | null {
  try {
    return asScheme(window.localStorage.getItem(THEME_KEY))
  } catch {
    return null
  }
}

/**
 * Read the two inputs, resolve, apply — the one call `main.tsx` makes.
 *
 * `localStorage` can throw (Safari's private mode, a disabled-cookie profile),
 * and a theme is not worth a blank page, so an unreadable store is simply an
 * absent override.
 */
export function initTheme(): Scheme {
  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(THEME_KEY)
  } catch {
    stored = null
  }
  const scheme = resolveScheme(stored, window.matchMedia(DARK_QUERY).matches)
  applyScheme(document.documentElement, scheme)
  return scheme
}
